import {
  Alert,
  Backdrop,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Icon,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Tooltip,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import QRCode from "react-qr-code";
import { useNavigate, useParams } from "react-router-dom";
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import usePageTitle from "app/pageTitle";
import MDInput from "theme/components/MDInput";
import MDButton from "theme/components/MDButton";
import DataTable from "theme/Tables/DataTable";
import axios from "axios.js";

function PredictPromotionQuestionPage() {
  usePageTitle("Predict & Win - Question");
  const navigate = useNavigate();
  const { ref } = useParams();
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");
  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [question, setQuestion] = useState(null);
  const [options, setOptions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [prizes, setPrizes] = useState([]);
  const [selectionMethods, setSelectionMethods] = useState([]);
  const [rewardTypes, setRewardTypes] = useState([]);
  const [outcome, setOutcome] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openPrizeDialog, setOpenPrizeDialog] = useState(false);
  const [editingPrize, setEditingPrize] = useState(null);
  const [questionText, setQuestionText] = useState("");
  const [questionType, setQuestionType] = useState("");
  const [answerType, setAnswerType] = useState("");
  const [answerValueType, setAnswerValueType] = useState("");
  const [selectionCardinality, setSelectionCardinality] = useState("");
  const [opensAt, setOpensAt] = useState("");
  const [closesAt, setClosesAt] = useState("");
  const [position, setPosition] = useState("0");
  const [prizeTitle, setPrizeTitle] = useState("");
  const [selectionMethod, setSelectionMethod] = useState("");
  const [rewardType, setRewardType] = useState("");
  const [rewardValue, setRewardValue] = useState("");
  const [selectionConfig, setSelectionConfig] = useState({});
  const [outcomeAnswer, setOutcomeAnswer] = useState("");
  const qrCodeRef = useRef(null);

  const selectedSelectionMethod = selectionMethods.find(
    (method) => method.reference === selectionMethod
  );
  const selectedRewardType = rewardTypes.find((type) => type.reference === rewardType);
  const isAmountReward = ["cash", "airtime"].some((name) =>
    (selectedRewardType?.title || "").toLowerCase().includes(name)
  );

  const getSelectionConfigDefaults = (method) =>
    (method?.config || []).reduce((config, field) => {
      config[field.name] = field.default ?? (field.type === "checkbox" ? false : "");
      return config;
    }, {});

  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const showMessage = (message, messageSeverity = "error") => {
    setSeverity(messageSeverity);
    setErrMsg(message || "An unexpected error occurred.");
    setOpen(true);
  };

  const handleRequestError = () => {
    setShowLoading(false);
    showMessage(
      `Unable to complete request. ${
        navigator.onLine ? "" : "Make sure you have an active internet connection."
      }`
    );
  };

  const toDateTimeInput = (value) => {
    if (!value) {
      return "";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "";
    }
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().slice(0, 16);
  };

  const formatDate = (value) => {
    if (!value) {
      return "---";
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
  };

  const prettyJson = (value) => {
    try {
      return JSON.stringify(value ?? {}, null, 2);
    } catch (error) {
      return "{}";
    }
  };

  const showStatus = (status) => (
    <MDTypography variant="h6" color={status ? "success" : "text"}>
      {status ? "Active" : "Paused"}
    </MDTypography>
  );

  const questionColumns = [
    { Header: "Option", accessor: "title", align: "left" },
    { Header: "Status", accessor: "status", align: "right" },
  ];

  const answerColumns = [
    { Header: "Option", accessor: "option", align: "left" },
    { Header: "Label", accessor: "label", align: "left" },
    { Header: "Value", accessor: "value", align: "left" },
    { Header: "Position", accessor: "position", align: "right" },
  ];

  const prizeColumns = [
    { Header: "Prize", accessor: "title", align: "left" },
    { Header: "Selection Method", accessor: "selection_method", align: "left" },
    { Header: "Reward Type", accessor: "reward_type", align: "left" },
    { Header: "Reward Value", accessor: "reward_value", align: "left" },
    { Header: "Status", accessor: "status", align: "center" },
    { Header: "Actions", accessor: "actions", align: "right" },
  ];

  const participantColumns = [
    { Header: "Name", accessor: "name", align: "left" },
    { Header: "Email", accessor: "email_address", align: "left" },
    { Header: "Phone", accessor: "phone_number", align: "left" },
    { Header: "Correct", accessor: "is_correct", align: "center" },
    { Header: "Submitted", accessor: "created", align: "right" },
  ];

  const predictionColumns = [
    { Header: "Participant", accessor: "participant", align: "left" },
    // { Header: "Answer", accessor: "answer", align: "left" },
    { Header: "Correct", accessor: "is_correct", align: "center" },
    { Header: "Submitted", accessor: "created", align: "right" },
  ];

  const winnerColumns = [
    { Header: "Participant", accessor: "participant", align: "left" },
    { Header: "Email", accessor: "email_address", align: "left" },
    { Header: "Phone", accessor: "phone_number", align: "left" },
    { Header: "Disbursed", accessor: "disbursed", align: "center" },
    { Header: "Created", accessor: "created", align: "left" },
  ];

  const fetchQuestion = async () => {
    setShowLoading(true);
    try {
      const result = await axios.get(`/portal/tm/predict/promotions/questions/${ref}/fetch.json`);
      if (result.data.status === "success") {
        const data = result.data.data;
        const questionData = data.question;

        setQuestion(questionData);
        setOptions(
          (data.options || []).map((row) => ({
            reference: row.reference,
            title: row.title || "---",
            value: row.value ?? row.title ?? "",
            position: row.position ?? "---",
            status: showStatus(row.status),
          }))
        );

        setAnswers(
          (data.answers || []).map((row) => ({
            reference: row.reference,
            option_reference: row.option?.reference || null,
            option: row.option?.title || "General",
            label: row.label || "---",
            value: row.value ?? "---",
            position: row.position ?? "---",
          }))
        );

        setPrizes(data.prizes || []);
        setSelectionMethods(data.selection_methods || []);
        setRewardTypes(data.reward_types || []);
        setOutcome(data.outcome || null);
        setQuestionText(questionData.question || "");
        setQuestionType(questionData.question_type || "");
        setAnswerType(questionData.answer_type || "");

        setActiveTab(
          questionData.question_type === "OPTIONS_ANSWERS"
            ? 0
            : questionData.answer_type === "controlled"
            ? 1
            : 2
        );

        setAnswerValueType(questionData.answer_value_type || "");
        setSelectionCardinality(questionData.selection_cardinality || "");
        setOpensAt(toDateTimeInput(questionData.opens_at));
        setClosesAt(toDateTimeInput(questionData.closes_at));
        setPosition(String(questionData.position ?? 0));
        setOutcomeAnswer(
          questionData.question_type === "OPTIONS_ANSWERS"
            ? data.outcome?.answer &&
              typeof data.outcome.answer === "object" &&
              !Array.isArray(data.outcome.answer)
              ? data.outcome.answer
              : {}
            : data.outcome?.answer ?? ""
        );
        setPageLoaded(true);
      } else {
        showMessage(result.data.data.message);
      }
    } catch (error) {
      handleRequestError();
      return;
    }
    setShowLoading(false);
  };

  const copyShareLink = async () => {
    const shareLink = question?.share_link;
    if (!shareLink) {
      showMessage("Share link is not available.");
      return;
    }

    try {
      await navigator.clipboard.writeText(shareLink);
      showMessage("Share link copied successfully.", "success");
    } catch (error) {
      const input = document.createElement("textarea");
      input.value = shareLink;
      input.style.position = "fixed";
      input.style.opacity = "0";
      document.body.appendChild(input);
      input.select();
      const copied = document.execCommand("copy");
      input.remove();
      showMessage(
        copied ? "Share link copied successfully." : "Unable to copy share link.",
        copied ? "success" : "error"
      );
    }
  };

  const downloadQrCode = () => {
    const svg = qrCodeRef.current?.querySelector("svg");
    if (!svg) {
      showMessage("QR code is not available to download.");
      return;
    }

    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
    const svgUrl = URL.createObjectURL(svgBlob);
    const image = new Image();

    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 600;
      canvas.height = 600;
      const context = canvas.getContext("2d");
      context.fillStyle = "#FFFFFF";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);

      const link = document.createElement("a");
      link.download = `predict-question-${ref}-qr.png`;
      link.href = canvas.toDataURL("image/png");
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(svgUrl);
    };

    image.onerror = () => {
      URL.revokeObjectURL(svgUrl);
      showMessage("Unable to generate QR code image.");
    };

    image.src = svgUrl;
  };

  const fetchParticipants = async () => {
    try {
      const result = await axios.get(
        `/portal/tm/predict/promotions/questions/${ref}/participants.json`
      );
      if (result.data.status === "success") {
        setParticipants(
          (result.data.data.participants || []).map((row) => ({
            name: row.participant?.name || "---",
            email_address: row.participant?.email_address || "---",
            phone_number: row.participant?.phone_number || "---",
            is_correct: row.is_correct ? "Yes" : "No",
            created: formatDate(row.created),
          }))
        );
      } else {
        showMessage(result.data.data.message);
      }
    } catch (error) {
      showMessage("Unable to load participants.");
    }
  };

  const fetchPredictions = async () => {
    try {
      const result = await axios.get(
        `/portal/tm/predict/promotions/questions/${ref}/predictions.json`
      );
      if (result.data.status === "success") {
        setPredictions(
          (result.data.data.predictions || []).map((row) => ({
            participant: row.participant?.name || "---",
            answer: prettyJson(row.answer),
            is_correct: row.is_correct ? "Yes" : "No",
            created: formatDate(row.created),
          }))
        );
      } else {
        showMessage(result.data.data.message);
      }
    } catch (error) {
      showMessage("Unable to load predictions.");
    }
  };

  useEffect(() => {
    if (ref) {
      fetchQuestion();
    }
  }, [ref, refreshPage]);

  useEffect(() => {
    if (activeTab === 3 && ref) {
      fetchParticipants();
    } else if (activeTab === 4 && ref) {
      fetchPredictions();
    }
  }, [activeTab, ref, refreshPage]);

  const editQuestion = async () => {
    if (!questionText.trim()) {
      showMessage("Enter the question.");
      return;
    }

    if (!opensAt || !closesAt) {
      showMessage("Select the opening and closing dates.");
      return;
    }

    if (new Date(closesAt) <= new Date(opensAt)) {
      showMessage("The closing date must be after the opening date.");
      return;
    }

    if (!Number.isInteger(Number(position)) || Number(position) < 0) {
      showMessage("Position must be a non-negative whole number.");
      return;
    }

    setOpenEditDialog(false);
    setShowLoading(true);

    try {
      const result = await axios.post("/portal/tm/predict/promotions/questions/edit.json", {
        reference: ref,
        question: questionText.trim(),
        question_type: questionType,
        answer_type: answerType,
        answer_value_type: answerValueType || null,
        selection_cardinality: selectionCardinality || null,
        opens_at: new Date(opensAt).toISOString(),
        closes_at: new Date(closesAt).toISOString(),
        position: Number(position),
      });

      if (result.data.status === "success") {
        showMessage(result.data.data.message || "Question updated successfully.", "success");
        setRefreshPage((previous) => !previous);
      } else {
        showMessage(result.data.data.message);
      }
    } catch (error) {
      handleRequestError();
      return;
    }

    setShowLoading(false);
  };

  const updateQuestionStatus = async () => {
    setShowLoading(true);
    try {
      const result = await axios.get(`/portal/tm/predict/promotions/questions/${ref}/status.json`);
      if (result.data.status === "success") {
        showMessage(result.data.data.message || "Question status updated successfully.", "success");
        setRefreshPage((previous) => !previous);
      } else {
        showMessage(result.data.data.message);
      }
    } catch (error) {
      handleRequestError();
      return;
    }
    setShowLoading(false);
  };

  const resetPrizeForm = () => {
    setEditingPrize(null);
    setPrizeTitle("");
    setSelectionMethod("");
    setRewardType("");
    setRewardValue("");
    setSelectionConfig({});
  };

  const openCreatePrize = () => {
    resetPrizeForm();
    setOpenPrizeDialog(true);
  };

  const openEditPrize = (prize) => {
    setEditingPrize(prize);
    setPrizeTitle(prize.title || "");
    setSelectionMethod(prize.selection_method?.reference || "");
    setRewardType(prize.reward_type?.reference || "");
    setRewardValue(
      prize.reward_value === null || prize.reward_value === undefined
        ? ""
        : String(prize.reward_value)
    );
    setSelectionConfig(
      prize.selection_config && typeof prize.selection_config === "object"
        ? { ...prize.selection_config }
        : {}
    );
    setOpenPrizeDialog(true);
  };

  const savePrize = async () => {
    if (!prizeTitle.trim()) {
      showMessage("Enter prize title.");
      return;
    }

    if (!selectionMethod) {
      showMessage("Select a selection method.");
      return;
    }

    if (!rewardType) {
      showMessage("Select a reward type.");
      return;
    }

    let parsedRewardValue = null;
    if (isAmountReward) {
      if (rewardValue === "" || !Number.isFinite(Number(rewardValue)) || Number(rewardValue) < 0) {
        showMessage("Enter a valid reward amount greater than or equal to zero.");
        return;
      }
      parsedRewardValue = Number(rewardValue);
    }

    const configFields = selectedSelectionMethod?.config || [];
    for (const field of configFields) {
      const value = selectionConfig[field.name];
      if (field.required && (value === undefined || value === null || value === "")) {
        showMessage(`Enter ${field.label || field.name}.`);
        return;
      }

      if (field.type === "number" && value !== "" && value !== undefined && value !== null) {
        const numberValue = Number(value);
        if (!Number.isFinite(numberValue)) {
          showMessage(`${field.label || field.name} must be a valid number.`);
          return;
        }

        if (field.min !== undefined && numberValue < field.min) {
          showMessage(`${field.label || field.name} must be at least ${field.min}.`);
          return;
        }

        if (field.max !== undefined && numberValue > field.max) {
          showMessage(`${field.label || field.name} must be no more than ${field.max}.`);
          return;
        }
      }
    }

    const parsedSelectionConfig = configFields.reduce((config, field) => {
      const value = selectionConfig[field.name];
      config[field.name] =
        field.type === "number" && value !== "" && value !== undefined && value !== null
          ? Number(value)
          : value;
      return config;
    }, {});

    setOpenPrizeDialog(false);
    setShowLoading(true);

    const payload = {
      title: prizeTitle.trim(),
      selection_method: selectionMethod,
      reward_type_reference: rewardType,
      reward_value: parsedRewardValue,
      selection_config: parsedSelectionConfig,
    };

    if (editingPrize) {
      payload.reference = editingPrize.reference;
    } else {
      payload.question_reference = ref;
    }

    try {
      const result = await axios.post(
        editingPrize
          ? "/portal/tm/predict/promotions/questions/prizes/edit.json"
          : "/portal/tm/predict/promotions/questions/prizes/create.json",
        payload
      );

      if (result.data.status === "success") {
        showMessage(result.data.data.message || "Prize saved successfully.", "success");
        resetPrizeForm();
        setRefreshPage((previous) => !previous);
      } else {
        showMessage(result.data.data.message);
      }
    } catch (error) {
      handleRequestError();
      return;
    }

    setShowLoading(false);
  };

  const updatePrizeStatus = async (prize) => {
    setShowLoading(true);
    try {
      const result = await axios.get(
        `/portal/tm/predict/promotions/questions/prizes/${prize.reference}/status.json`
      );
      if (result.data.status === "success") {
        showMessage(result.data.data.message || "Prize status updated successfully.", "success");
        setRefreshPage((previous) => !previous);
      } else {
        showMessage(result.data.data.message);
      }
    } catch (error) {
      handleRequestError();
      return;
    }
    setShowLoading(false);
  };

  const deletePrize = async (prize) => {
    if (!window.confirm(`Delete prize "${prize.title}"?`)) {
      return;
    }

    setShowLoading(true);
    try {
      const result = await axios.get(
        `/portal/tm/predict/promotions/questions/prizes/${prize.reference}/delete.json`
      );
      if (result.data.status === "success") {
        showMessage(result.data.data.message || "Prize deleted successfully.", "success");
        setRefreshPage((previous) => !previous);
      } else {
        showMessage(result.data.data.message);
      }
    } catch (error) {
      handleRequestError();
      return;
    }

    setShowLoading(false);
  };

  const getAnswerInputType = () => {
    const type = (answerValueType || "text").toLowerCase();
    if (["number", "integer", "int", "float", "decimal", "currency", "percentage"].includes(type))
      return "number";
    if (["date"].includes(type)) return "date";
    if (["datetime", "datetime-local", "timestamp"].includes(type)) return "datetime-local";
    if (["time"].includes(type)) return "time";
    return "text";
  };

  const getTypedOutcomeValue = (value) => {
    if (value === "" || value === null || value === undefined) return value;
    const type = (answerValueType || "text").toLowerCase();
    if (["number", "integer", "int", "float", "decimal", "currency", "percentage"].includes(type)) {
      return Number(value);
    }
    if (["boolean", "bool"].includes(type)) return value === true || value === "true";
    return value;
  };

  const isMultiSelect = () =>
    String(selectionCardinality || question?.selection_cardinality || "")
      .toLowerCase()
      .includes("multi");

  const getControlledAnswers = () => answers;

  const getOptionControlledAnswers = (option) => {
    const matchingAnswers = answers.filter(
      (answer) => answer.option_reference === option.reference
    );
    return matchingAnswers.length ? matchingAnswers : answers;
  };

  const getOptionOutcomeValue = (option) => {
    if (!outcomeAnswer || typeof outcomeAnswer !== "object" || Array.isArray(outcomeAnswer)) {
      return "";
    }
    const value = outcomeAnswer[option.reference];
    if (value === null || value === undefined) {
      return isMultiSelect() ? [] : "";
    }
    return value;
  };

  const handleOptionOutcomeChange = (option, value) => {
    setOutcomeAnswer((previous) => {
      const current =
        previous && typeof previous === "object" && !Array.isArray(previous) ? previous : {};
      return { ...current, [option.reference]: value };
    });
  };

  const getOutcomeInputType = () => {
    const type = (answerValueType || "text").toLowerCase();
    if (["number", "integer", "int", "float", "decimal", "currency", "percentage"].includes(type)) {
      return "number";
    }
    if (type === "date") return "date";
    if (["datetime", "datetime-local", "timestamp"].includes(type)) return "datetime-local";
    if (type === "time") return "time";
    return "text";
  };

  const saveOutcome = async () => {
    let parsedAnswer;

    if (question?.question_type === "OPTIONS_ANSWERS" && options.length > 0) {
      const optionOutcomes =
        outcomeAnswer && typeof outcomeAnswer === "object" && !Array.isArray(outcomeAnswer)
          ? outcomeAnswer
          : {};

      for (const option of options) {
        const value = optionOutcomes[option.reference];
        if (
          value === undefined ||
          value === null ||
          value === "" ||
          (Array.isArray(value) && value.length === 0)
        ) {
          showMessage(`Enter the official outcome for "${option.title}".`);
          return;
        }
      }

      parsedAnswer = options.reduce((result, option) => {
        const value = optionOutcomes[option.reference];
        if (answerType.toLowerCase() === "controlled") {
          result[option.reference] = Array.isArray(value)
            ? value.map((item) => {
                const answer = getOptionControlledAnswers(option).find(
                  (itemAnswer) => String(itemAnswer.value) === String(item)
                );
                return getTypedOutcomeValue(answer ? answer.value : item);
              })
            : getTypedOutcomeValue(
                getOptionControlledAnswers(option).find(
                  (itemAnswer) => String(itemAnswer.value) === String(value)
                )?.value ?? value
              );
        } else {
          result[option.reference] = getTypedOutcomeValue(value);
        }
        return result;
      }, {});
    } else if (answerType.toLowerCase() === "controlled") {
      if (isMultiSelect()) {
        if (!Array.isArray(outcomeAnswer) || outcomeAnswer.length === 0) {
          showMessage("Select at least one official outcome.");
          return;
        }

        parsedAnswer = outcomeAnswer.map((value) => {
          const answer = answers.find((item) => String(item.value) === String(value));
          return getTypedOutcomeValue(answer ? answer.value : value);
        });
      } else {
        if (outcomeAnswer === "" || outcomeAnswer === null || outcomeAnswer === undefined) {
          showMessage("Select the official outcome.");
          return;
        }

        const answer = answers.find((item) => String(item.value) === String(outcomeAnswer));
        parsedAnswer = getTypedOutcomeValue(answer ? answer.value : outcomeAnswer);
      }
    } else {
      if (outcomeAnswer === "" || outcomeAnswer === null || outcomeAnswer === undefined) {
        showMessage("Enter the official outcome.");
        return;
      }

      parsedAnswer = getTypedOutcomeValue(outcomeAnswer);
    }

    setShowLoading(true);
    try {
      const result = await axios.post("/portal/tm/predict/promotions/questions/outcome.json", {
        question_reference: ref,
        answer: parsedAnswer,
      });

      if (result.data.status === "success") {
        showMessage(result.data.data.message || "Outcome saved successfully.", "success");
        setRefreshPage((previous) => !previous);
      } else {
        showMessage(result.data.data.message);
      }
    } catch (error) {
      handleRequestError();
      return;
    }

    setShowLoading(false);
  };

  const exportCsv = async (url, filename) => {
    setShowLoading(true);
    try {
      const result = await axios.get(url, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(
        new Blob([result.data], { type: "text/csv;charset=utf-8;" })
      );
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      handleRequestError();
      return;
    }

    setShowLoading(false);
  };

  const buildPrizeRows = (data) =>
    data.map((prize) => ({
      title: prize.title,
      selection_method: prize.selection_method?.title || "---",
      reward_type: prize.reward_type?.title || "---",
      reward_value: prize.reward_value || "---",
      status: showStatus(prize.status),
      actions: (
        <MDBox display="flex" justifyContent="flex-end" gap={1}>
          <Tooltip title="Edit Prize">
            <MDButton
              size="small"
              variant="outlined"
              color="dark"
              iconOnly
              onClick={() => openEditPrize(prize)}
            >
              <Icon fontSize="small">edit</Icon>
            </MDButton>
          </Tooltip>
          <Tooltip title={prize.status ? "Pause Prize" : "Activate Prize"}>
            <MDButton
              size="small"
              variant="outlined"
              color={prize.status ? "warning" : "success"}
              iconOnly
              onClick={() => updatePrizeStatus(prize)}
            >
              <Icon fontSize="small">{prize.status ? "pause" : "play_arrow"}</Icon>
            </MDButton>
          </Tooltip>
          <Tooltip title="View Winners">
            <MDButton
              size="small"
              variant="outlined"
              color="dark"
              iconOnly
              onClick={() => navigate(`/tm/predict/prizes/${prize.reference}/winners`)}
            >
              <Icon fontSize="small">emoji_events</Icon>
            </MDButton>
          </Tooltip>
          <Tooltip title="Delete Prize">
            <MDButton
              size="small"
              variant="outlined"
              color="error"
              iconOnly
              onClick={() => deletePrize(prize)}
            >
              <Icon fontSize="small">delete</Icon>
            </MDButton>
          </Tooltip>
        </MDBox>
      ),
    }));

  const fetchWinners = async (prize) => {
    navigate(`/tm/predict/prizes/${prize.reference}/winners`);
  };
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <MDBox>
            <MDTypography variant="h4" fontWeight="medium">
              {question?.question || "Question"}
            </MDTypography>
            <MDTypography variant="button" color="text">
              {question?.promotion?.title || "Predict & Win"} · Question management
            </MDTypography>
          </MDBox>

          <MDBox display="flex" gap={1}>
            <MDButton
              variant="outlined"
              color="dark"
              startIcon={<Icon>west</Icon>}
              onClick={() => navigate(-1)}
              size="small"
            >
              Back
            </MDButton>

            <MDButton
              variant="outlined"
              color={question?.status ? "warning" : "success"}
              onClick={updateQuestionStatus}
              disabled={!question}
              startIcon={<Icon>{question?.status ? "pause" : "play_arrow"}</Icon>}
              size="small"
            >
              {question?.status ? "Pause" : "Activate"}
            </MDButton>

            <MDButton
              variant="contained"
              color="dark"
              startIcon={<Icon>edit</Icon>}
              onClick={() => setOpenEditDialog(true)}
              disabled={!question}
              size="small"
            >
              Edit Question
            </MDButton>
          </MDBox>
        </MDBox>

        {question && (
          <MDBox
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "minmax(0, 1.5fr) minmax(300px, 1fr)" }}
            gap={3}
            mb={3}
          >
            <Card>
              <MDBox p={3}>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  gap={2}
                >
                  <MDBox>
                    <MDTypography variant="h6">Question Details</MDTypography>
                    <MDTypography variant="button" color="text">
                      {question.question}
                    </MDTypography>
                  </MDBox>
                  {showStatus(question.status)}
                </MDBox>

                <MDBox mt={2}>
                  <Stack>
                    <MDTypography variant="caption" color="text">
                      Prediction Window:
                    </MDTypography>
                    <MDBox>
                      <MDTypography variant="button">{formatDate(question.opens_at)}</MDTypography>
                      <MDTypography variant="caption" color="text">
                        &nbsp; <Icon>east</Icon> &nbsp;
                      </MDTypography>
                      <MDTypography variant="button">{formatDate(question.closes_at)}</MDTypography>
                    </MDBox>
                  </Stack>
                </MDBox>
              </MDBox>
            </Card>

            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" mb={1}>
                  Share Question
                </MDTypography>
                <MDTypography variant="button" color="text" display="block" mb={2}>
                  Share this link or let participants scan the QR code to submit their prediction.
                </MDTypography>

                {question.share_link ? (
                  <>
                    <MDBox
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      p={2}
                      mb={2}
                      sx={{
                        border: "1px solid #eeeeee",
                        borderRadius: 1,
                        backgroundColor: "#ffffff",
                      }}
                      ref={qrCodeRef}
                    >
                      <QRCode
                        value={question.share_link}
                        size={190}
                        level="H"
                        bgColor="#FFFFFF"
                        fgColor="#000000"
                      />
                    </MDBox>

                    {/* <MDInput
                      label="Share Link"
                      value={question.share_link}
                      fullWidth
                      InputProps={{ readOnly: true }}
                      sx={{ mb: 2 }}
                    /> */}

                    <MDBox display="flex" gap={1} flexWrap="wrap" justifyContent="center">
                      <MDButton
                        variant="contained"
                        color="dark"
                        startIcon={<Icon>content_copy</Icon>}
                        onClick={copyShareLink}
                        size="small"
                      >
                        Copy
                      </MDButton>
                      <MDButton
                        variant="outlined"
                        color="dark"
                        startIcon={<Icon>download</Icon>}
                        onClick={downloadQrCode}
                        size="small"
                      >
                        Download
                      </MDButton>
                      <MDButton
                        component="a"
                        href={question.share_link || "#"}
                        target="_blank"
                        rel="noreferrer"
                        color="dark"
                        variant="outlined"
                        size="small"
                        startIcon={<Icon>open_in_new</Icon>}
                        disabled={!question.share_link}
                      >
                        Open
                      </MDButton>
                    </MDBox>
                  </>
                ) : (
                  <MDTypography variant="button" color="text">
                    Share link is not available for this question.
                  </MDTypography>
                )}
              </MDBox>
            </Card>
          </MDBox>
        )}

        <Card>
          <MDBox px={2} pt={2}>
            <Tabs
              value={activeTab}
              onChange={(event, value) => setActiveTab(value)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {question?.question_type === "OPTIONS_ANSWERS" && <Tab value={0} label="Options" />}
              {question?.answer_type?.toLowerCase() === "controlled" && (
                <Tab value={1} label="Answers" />
              )}
              <Tab value={2} label="Prizes" />
              <Tab value={3} label="Participants" />
              <Tab value={4} label="Predictions" />
              <Tab value={5} label="Outcome" />
            </Tabs>
          </MDBox>

          <MDBox p={3}>
            {question?.question_type === "OPTIONS_ANSWERS" && activeTab === 0 && (
              <>
                <MDTypography variant="h6" mb={2}>
                  Question Options
                </MDTypography>

                <DataTable
                  table={{ columns: questionColumns, rows: options }}
                  canSearch={false}
                  showTotalEntries
                  entriesPerPage={false}
                  isSorted={false}
                />

                {options.length === 0 && (
                  <MDTypography variant="button" color="text">
                    No options are configured for this question.
                  </MDTypography>
                )}
              </>
            )}

            {question?.answer_type?.toLowerCase() === "controlled" && activeTab === 1 && (
              <>
                <MDTypography variant="h6" mb={2}>
                  Controlled Answers
                </MDTypography>

                <DataTable
                  table={{ columns: answerColumns, rows: answers }}
                  canSearch={false}
                  showTotalEntries
                  entriesPerPage={false}
                  isSorted={false}
                />

                {answers.length === 0 && (
                  <MDTypography variant="button" color="text">
                    No controlled answers are configured for this question.
                  </MDTypography>
                )}
              </>
            )}

            {activeTab === 2 && (
              <>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <MDTypography variant="h6">Prizes</MDTypography>
                  <MDButton
                    variant="contained"
                    color="dark"
                    startIcon={<Icon>add</Icon>}
                    onClick={openCreatePrize}
                    size="small"
                  >
                    Add Prize
                  </MDButton>
                </MDBox>

                <DataTable
                  table={{ columns: prizeColumns, rows: buildPrizeRows(prizes) }}
                  canSearch={false}
                  showTotalEntries
                  entriesPerPage={false}
                  isSorted={false}
                />

                {prizes.length === 0 && (
                  <MDTypography variant="button" color="text">
                    No prizes have been configured.
                  </MDTypography>
                )}
              </>
            )}

            {activeTab === 3 && (
              <>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <MDTypography variant="h6">Participants</MDTypography>
                  <MDButton
                    variant="outlined"
                    color="dark"
                    onClick={() =>
                      exportCsv(
                        `/portal/tm/predict/questions/${ref}/participants/export.json`,
                        `participants_${ref}.csv`
                      )
                    }
                    size="small"
                    startIcon={<Icon>download</Icon>}
                  >
                    Export
                  </MDButton>
                </MDBox>

                <DataTable
                  table={{ columns: participantColumns, rows: participants }}
                  canSearch
                  showTotalEntries
                  entriesPerPage={false}
                  isSorted={false}
                />

                {participants.length === 0 && (
                  <MDTypography variant="button" color="text">
                    No participants found.
                  </MDTypography>
                )}
              </>
            )}

            {activeTab === 4 && (
              <>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <MDTypography variant="h6">Predictions</MDTypography>
                  <MDButton
                    variant="outlined"
                    color="dark"
                    onClick={() =>
                      exportCsv(
                        `/portal/tm/predict/questions/${ref}/predictions/export.json`,
                        `predictions_${ref}.csv`
                      )
                    }
                    size="small"
                    startIcon={<Icon>download</Icon>}
                  >
                    Export
                  </MDButton>
                </MDBox>

                <DataTable
                  table={{ columns: predictionColumns, rows: predictions }}
                  canSearch
                  showTotalEntries
                  entriesPerPage={false}
                  isSorted={false}
                />

                {predictions.length === 0 && (
                  <MDTypography variant="button" color="text">
                    No predictions found.
                  </MDTypography>
                )}
              </>
            )}

            {activeTab === 5 && (
              <MDBox display="flex" flexDirection="column" gap={2}>
                <MDTypography variant="h6">Official Outcome</MDTypography>

                <MDTypography variant="button" color="text">
                  Enter the official outcome for this question. Saving marks it as pending
                  evaluation.
                </MDTypography>

                {question?.question_type === "OPTIONS_ANSWERS" && options.length > 0 ? (
                  <MDBox display="flex" flexDirection="column" gap={2.5}>
                    {options.map((option) => {
                      const fieldValue = getOptionOutcomeValue(option);
                      const optionAnswers = getOptionControlledAnswers(option);
                      const fieldLabel = `Outcome for ${option.title}`;

                      if (answerType.toLowerCase() === "controlled") {
                        return (
                          <FormControl key={option.reference} fullWidth>
                            <InputLabel>{fieldLabel}</InputLabel>
                            <Select
                              label={fieldLabel}
                              multiple={isMultiSelect()}
                              value={
                                isMultiSelect()
                                  ? Array.isArray(fieldValue)
                                    ? fieldValue.map(String)
                                    : fieldValue === ""
                                    ? []
                                    : [String(fieldValue)]
                                  : fieldValue === null || fieldValue === undefined
                                  ? ""
                                  : String(fieldValue)
                              }
                              onChange={(event) =>
                                handleOptionOutcomeChange(option, event.target.value)
                              }
                              sx={{ padding: "12px", paddingLeft: "3px" }}
                              disabled={
                                question?.closes_at && new Date(question.closes_at) > new Date()
                              }
                            >
                              {optionAnswers.map((answer) => (
                                <MenuItem
                                  key={answer.reference || answer.value}
                                  value={String(answer.value)}
                                >
                                  {answer.label && answer.label !== "---"
                                    ? `${answer.label} (${answer.value})`
                                    : answer.value}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        );
                      }

                      if (["boolean", "bool"].includes((answerValueType || "").toLowerCase())) {
                        return (
                          <FormControl key={option.reference} fullWidth>
                            <InputLabel>{fieldLabel}</InputLabel>
                            <Select
                              label={fieldLabel}
                              value={
                                fieldValue === true
                                  ? "true"
                                  : fieldValue === false
                                  ? "false"
                                  : String(fieldValue ?? "")
                              }
                              onChange={(event) =>
                                handleOptionOutcomeChange(option, event.target.value)
                              }
                              sx={{ padding: "12px", paddingLeft: "3px" }}
                              disabled={
                                question?.closes_at && new Date(question.closes_at) > new Date()
                              }
                            >
                              <MenuItem value="true">True</MenuItem>
                              <MenuItem value="false">False</MenuItem>
                            </Select>
                          </FormControl>
                        );
                      }

                      return (
                        <MDInput
                          key={option.reference}
                          label={fieldLabel}
                          type={getOutcomeInputType()}
                          value={fieldValue ?? ""}
                          onChange={(event) =>
                            handleOptionOutcomeChange(option, event.target.value)
                          }
                          fullWidth
                          disabled={
                            question?.closes_at && new Date(question.closes_at) > new Date()
                          }
                          inputProps={getOutcomeInputType() === "number" ? { step: "any" } : {}}
                        />
                      );
                    })}
                  </MDBox>
                ) : answerType.toLowerCase() === "controlled" ? (
                  <FormControl fullWidth>
                    <InputLabel>Official Outcome</InputLabel>
                    <Select
                      label="Official Outcome"
                      multiple={isMultiSelect()}
                      value={
                        isMultiSelect()
                          ? Array.isArray(outcomeAnswer)
                            ? outcomeAnswer.map(String)
                            : outcomeAnswer === ""
                            ? []
                            : [String(outcomeAnswer)]
                          : outcomeAnswer === null || outcomeAnswer === undefined
                          ? ""
                          : String(outcomeAnswer)
                      }
                      onChange={(event) => setOutcomeAnswer(event.target.value)}
                      sx={{ padding: "12px", paddingLeft: "3px" }}
                    >
                      {getControlledAnswers().map((answer) => (
                        <MenuItem
                          key={answer.reference || answer.value}
                          value={String(answer.value)}
                        >
                          {answer.label && answer.label !== "---"
                            ? `${answer.label} (${answer.value})`
                            : answer.value}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : answerValueType.toLowerCase() === "boolean" ||
                  answerValueType.toLowerCase() === "bool" ? (
                  <FormControl fullWidth>
                    <InputLabel>Official Outcome</InputLabel>
                    <Select
                      label="Official Outcome"
                      value={
                        outcomeAnswer === true
                          ? "true"
                          : outcomeAnswer === false
                          ? "false"
                          : String(outcomeAnswer)
                      }
                      onChange={(event) => setOutcomeAnswer(event.target.value)}
                      sx={{ padding: "12px", paddingLeft: "3px" }}
                    >
                      <MenuItem value="true">True</MenuItem>
                      <MenuItem value="false">False</MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <MDInput
                    label="Official Outcome"
                    type={getAnswerInputType()}
                    value={outcomeAnswer}
                    onChange={(event) => setOutcomeAnswer(event.target.value)}
                    fullWidth
                    inputProps={getAnswerInputType() === "number" ? { step: "any" } : {}}
                  />
                )}

                {outcome && (
                  <MDTypography variant="caption" color={outcome.is_ready ? "success" : "warning"}>
                    Evaluation status: {outcome.is_ready ? "Ready" : "Pending evaluation"}
                  </MDTypography>
                )}

                <MDBox>
                  <MDButton
                    variant="contained"
                    color="dark"
                    onClick={saveOutcome}
                    disabled={question?.closes_at && new Date(question.closes_at) > new Date()}
                  >
                    Save Outcome
                  </MDButton>
                </MDBox>
              </MDBox>
            )}
          </MDBox>
        </Card>
      </MDBox>
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Question</DialogTitle>
        <DialogContent>
          <MDBox display="flex" flexDirection="column" gap={2.4} pt={1}>
            <MDInput
              label="Question"
              value={questionText}
              onChange={(event) => setQuestionText(event.target.value)}
              fullWidth
              multiline
              rows={3}
            />

            <MDInput
              label="Opens At"
              type="datetime-local"
              value={opensAt}
              onChange={(event) => setOpensAt(event.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <MDInput
              label="Closes At"
              type="datetime-local"
              value={closesAt}
              onChange={(event) => setClosesAt(event.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton variant="outlined" color="dark" onClick={() => setOpenEditDialog(false)}>
            Cancel
          </MDButton>
          <MDButton variant="contained" color="dark" onClick={editQuestion}>
            Save Changes
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openPrizeDialog}
        onClose={() => setOpenPrizeDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{editingPrize ? "Edit Prize" : "Create Prize"}</DialogTitle>
        <DialogContent>
          <MDBox display="flex" flexDirection="column" gap={2.4} pt={1}>
            <MDInput
              label="Prize Name"
              value={prizeTitle}
              onChange={(event) => setPrizeTitle(event.target.value)}
              fullWidth
            />

            <MDTypography variant="h6">Selection Setup</MDTypography>

            <FormControl sx={{ minWidth: 120, width: "100%" }}>
              <InputLabel>Selection Method</InputLabel>
              <Select
                label="Selection Method"
                value={selectionMethod}
                onChange={(event) => {
                  const methodReference = event.target.value;
                  const method = selectionMethods.find(
                    (item) => item.reference === methodReference
                  );
                  setSelectionMethod(methodReference);
                  setSelectionConfig(getSelectionConfigDefaults(method));
                }}
                sx={{ padding: "12px", paddingLeft: "3px" }}
              >
                {selectionMethods.map((method) => (
                  <MenuItem key={method.reference} value={method.reference}>
                    <b>{method.title}</b> — {`Example: ${method.example}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedSelectionMethod &&
              (selectedSelectionMethod.config || []).map((field) => (
                <MDBox key={field.name}>
                  {field.type === "select" ? (
                    <FormControl fullWidth>
                      <InputLabel>{field.label || field.name}</InputLabel>
                      <Select
                        label={field.label || field.name}
                        value={selectionConfig[field.name] ?? ""}
                        onChange={(event) =>
                          setSelectionConfig((previous) => ({
                            ...previous,
                            [field.name]: event.target.value,
                          }))
                        }
                        sx={{ padding: "12px", paddingLeft: "3px" }}
                      >
                        <MenuItem value="" disabled>
                          Select {field.label || field.name}
                        </MenuItem>
                        {(field.options || []).map((option) => (
                          <MenuItem key={String(option.value)} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <MDInput
                      label={field.label || field.name}
                      type={field.type === "number" ? "number" : "text"}
                      value={selectionConfig[field.name] ?? ""}
                      onChange={(event) =>
                        setSelectionConfig((previous) => ({
                          ...previous,
                          [field.name]: event.target.value,
                        }))
                      }
                      fullWidth
                      required={Boolean(field.required)}
                      placeholder={field.placeholder || ""}
                      inputProps={{
                        ...(field.min !== undefined ? { min: field.min } : {}),
                        ...(field.max !== undefined ? { max: field.max } : {}),
                        ...(field.step !== undefined ? { step: field.step } : {}),
                      }}
                      multiline={field.type === "textarea"}
                      minRows={field.type === "textarea" ? 3 : undefined}
                    />
                  )}

                  {field.description && (
                    <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                      {field.description}
                    </MDTypography>
                  )}
                </MDBox>
              ))}

            <MDTypography variant="h6">Reward Setup</MDTypography>

            <FormControl sx={{ minWidth: 120, width: "100%" }}>
              <InputLabel>Reward Type</InputLabel>
              <Select
                label="Reward Type"
                value={rewardType}
                onChange={(event) => {
                  setRewardType(event.target.value);
                  setRewardValue("");
                }}
                sx={{ padding: "12px", paddingLeft: "3px" }}
              >
                {rewardTypes.map((type) => (
                  <MenuItem key={type.reference} value={type.reference}>
                    {type.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {isAmountReward && (
              <MDInput
                label="Reward Amount"
                type="number"
                value={rewardValue}
                onChange={(event) => setRewardValue(event.target.value)}
                fullWidth
                required
                inputProps={{ min: 0, step: "any" }}
              />
            )}

            {selectedRewardType?.title?.toLowerCase().includes("voucher") && (
              <MDTypography variant="caption" color="text">
                Voucher availability is determined automatically from uploaded voucher codes. No
                reward amount is required.
              </MDTypography>
            )}

            {rewardType &&
              !isAmountReward &&
              !selectedRewardType?.title?.toLowerCase().includes("voucher") && (
                <MDTypography variant="caption" color="text">
                  This reward is fulfilled offline by the client. No reward amount is required.
                </MDTypography>
              )}
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton
            variant="outlined"
            color="dark"
            onClick={() => {
              setOpenPrizeDialog(false);
              resetPrizeForm();
            }}
          >
            Cancel
          </MDButton>
          <MDButton variant="contained" color="dark" onClick={savePrize}>
            Save Prize
          </MDButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Footer />
    </DashboardLayout>
  );
}

export default PredictPromotionQuestionPage;
